import Button from '@components/Button';
import ColorPicker from '@components/ColorPicker';
import Image from '@components/Image';
import WidgetRow from '@components/PlayerWidgets/WidgetRow';
import Slider from '@components/Slider';
import Tabbar from '@components/Tabbar';
import { MARKER_TITLE_DEFAULT } from '@modules/sandTable/constants';
import { isMapMarkerWithTitleSetting } from '@modules/sandTable/utils/markerTypeCheck';
import { $editingMapMarkerApi } from '@stores/editingMarkerStores';
import { type HSTitleSetting, ImageType } from '@types';
import { useMemo } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useLocalStorage } from 'react-use';
import { IconGradient, IconPure } from './icons';
import { presetList } from './presetList';

type ColorKey = keyof Pick<
  HSTitleSetting['background'],
  'bgColor' | 'borderColor' | 'gradientStartColor' | 'gradientEndColor'
>;
const bgDefault = MARKER_TITLE_DEFAULT.background;
const fontDefault = MARKER_TITLE_DEFAULT.font;

const styleList = [
  { id: 0, url: '/hotspots/special/a.png' },
  { id: 1, url: '/hotspots/special/b.png' },
  { id: 2, url: '/hotspots/special/c.png', fontColor: '255,243,216,1' },
  { id: 3, url: '/hotspots/special/d.png', fontColor: '255,243,216,1' },
  { id: 4, url: '/hotspots/special/e.png', fontColor: '237,246,232,1' },
  { id: 5, url: '/hotspots/special/f.png', fontColor: '255,216,186,1' },
  { id: 6, url: '/hotspots/special/g.png', fontColor: '182,193,205,1' },
  { id: 7, url: '/hotspots/special/h.png' },
  { id: 8, url: '/hotspots/special/i.png', fontColor: '217,217,217,1' },
];

const presetStyle = (
  bgData: Partial<HSTitleSetting['background']> = {},
  fontData: Partial<HSTitleSetting['font']> = {}
): React.CSSProperties => {
  const bg = { ...bgDefault, ...bgData };
  const font = { ...fontDefault, ...fontData };
  return {
    background:
      bg.type === 1
        ? `linear-gradient(${bg.gradientAngle}deg, rgba(${bg.gradientStartColor}), rgba(${bg.gradientEndColor}))`
        : `rgba(${bg.bgColor})`,
    color: `rgba(${font.fontColor})`,
    fontSize: 12,
    border: `${bg.borderWidth ? 1 : 0}px solid rgba(${bg.borderColor})`,
    borderRadius: bg.bgRoundedge,
  };
};

export function TitleStylesSetting({
  titleSetting,
}: { titleSetting: HSTitleSetting }) {
  const { styleType = 0, background, font } = titleSetting;
  const {
    type: bgType = 0,
    gradientAngle = bgDefault.gradientAngle,
    bgRoundedge = bgDefault.bgRoundedge,
    borderWidth = bgDefault.borderWidth,
  } = background;
  const [preset, setPreset] = useLocalStorage<typeof presetList>(
    '720_title_styles_preset',
    []
  );

  const [allPresetList, customList] = useMemo(() => {
    const list = (preset ?? []) as typeof presetList;
    return [[...list, ...presetList], list];
  }, [preset]);

  const handleBgTypeChange = (index: number) => {
    $editingMapMarkerApi.update((draft) => {
      if (isMapMarkerWithTitleSetting(draft)) {
        draft.titleSetting.background.type = index === 1 ? 1 : 0;
      }
    });
  };

  const handleSavePreset = () => {
    const newItem: (typeof presetList)[number] = {
      text: '自定义',
      isCustom: true,
      background,
      font,
    };
    setPreset([newItem, ...customList]);
  };

  const handleDeletePreset = (index: number) => {
    const newList = [...customList];
    newList.splice(index, 1);
    setPreset(newList);
  };

  const tabs = [
    <IconPure active={bgType === 0} />,
    <IconGradient active={bgType === 1} />,
  ];

  const renderColorSetting = (k: ColorKey) => {
    const val = background[k] ?? bgDefault[k];
    return (
      <ColorPicker
        className="mt-10"
        colorString={val}
        onChange={(colorString) => {
          $editingMapMarkerApi.update((draft) => {
            if (isMapMarkerWithTitleSetting(draft)) {
              draft.titleSetting.background[k] = colorString;
            }
          });
        }}
      >
        <ColorPicker.Bar />
      </ColorPicker>
    );
  };

  const renderGradientSetting = () => {
    return (
      <>
        {renderColorSetting('gradientStartColor')}
        {renderColorSetting('gradientEndColor')}
        <Slider
          className="mt-20"
          value={gradientAngle}
          min={0}
          max={360}
          valToString={(val) => `${val}°`}
          onChange={(val) => {
            $editingMapMarkerApi.update((draft) => {
              if (isMapMarkerWithTitleSetting(draft)) {
                draft.titleSetting.background.type = 1;
                draft.titleSetting.background.gradientAngle = val;
              }
            });
          }}
        >
          <Slider.Header title="渐变角度：" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
      </>
    );
  };

  const renderBasic = () => {
    return (
      <WidgetRow>
        <div className="mb-10">背景色：</div>
        <Tabbar
          tabs={tabs}
          tips={['纯色', '渐变色']}
          activeIndex={bgType}
          onChange={handleBgTypeChange}
        />
        {bgType === 0 && renderColorSetting('bgColor')}
        {bgType === 1 && renderGradientSetting()}
        <Slider
          className="mt-20"
          value={bgRoundedge}
          min={0}
          max={200}
          onChange={(val) => {
            $editingMapMarkerApi.update((draft) => {
              if (isMapMarkerWithTitleSetting(draft)) {
                draft.titleSetting.background.bgRoundedge = val;
              }
            });
          }}
        >
          <Slider.Header title="背景圆角大小：" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
        <Slider
          className="mt-20"
          value={borderWidth}
          min={0}
          max={10}
          valToString={(val) => `${val}px`}
          onChange={(val) => {
            $editingMapMarkerApi.update((draft) => {
              if (isMapMarkerWithTitleSetting(draft)) {
                draft.titleSetting.background.borderWidth = val;
              }
            });
          }}
        >
          <Slider.Header title="背景边框粗细：" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
        <div className="mt-20">背景边框颜色：</div>
        {renderColorSetting('borderColor')}
        <Button
          size="sm"
          scheme="dark"
          className="mt-20 w-full"
          disabled={customList.length >= 20}
          onClick={handleSavePreset}
        >
          {customList.length >= 20 ? '预设已到最大值' : '添加为预设'}
        </Button>
      </WidgetRow>
    );
  };

  const renderPreset = () => {
    return (
      <div className="h-120 rounded border border-black-#34 bg-black">
        <Scrollbars>
          <div
            className="flex flex-wrap items-center justify-between"
            style={{ padding: '10px 8px' }}
          >
            {allPresetList.map((x, i) => (
              <div
                key={i}
                className="group relative mb-15 flex h-30 w-65 cursor-pointer select-none items-center justify-center rounded"
                style={presetStyle(x.background, x.font)}
                onClick={() => {
                  $editingMapMarkerApi.update((draft) => {
                    if (isMapMarkerWithTitleSetting(draft)) {
                      draft.titleSetting.styleType = 0;
                      draft.titleSetting.background = {
                        ...bgDefault,
                        ...x.background,
                      };
                      draft.titleSetting.font = {
                        ...fontDefault,
                        ...x.font,
                      };
                    }
                  });
                }}
              >
                {x.text}
                {!!x.isCustom && (
                  <Image
                    src="/imgs/transh.c97645fca740ff9a9c95157a47284f0c.png"
                    type={ImageType.sysMedia}
                    className="absolute top-0 right-0 hidden h-16 w-16 hover:bg-blue group-hover:block"
                    style={{ width: 16, height: 16 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePreset(i);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </Scrollbars>
      </div>
    );
  };

  return (
    <>
      <WidgetRow>{styleType === 0 && renderPreset()}</WidgetRow>
      {styleType === 0 && renderBasic()}
    </>
  );
}
